import { Input } from '@mui/material';
import DSBox from '../../../design-system/DSBox';
import DSIconButton from '../../../design-system/DSIconButton';
import { DSTypography } from '../../../design-system/Typography';
import DSLabelTypography from '../../../design-system/Typography/DSLabelTypography';
import DSValueTypography from '../../../design-system/Typography/DSValueTypography';
import AddIcon from '@mui/icons-material/Add';
import useKlydoFileInput from '../hooks/useKlydoFileInput';
import CloseIcon from '@mui/icons-material/Close';
import KlydoLoopUrl from '../../../components/klydoPreview/KlydoLoopUrl';
import DSCircularProgress from '../../../design-system/DSCircularProgress';
import DSTooltip from '../../../design-system/DSTooltip';
import HelpOutline from '@mui/icons-material/HelpOutline';
import DSPaper from '../../../design-system/Paper/DSPaper';
import { UploadLoopSpeck } from './UploadLoopSpec';

type KlydoFileInputProps = {
  klydoSecureUrl: string;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: () => void;
  fileName: string;
  isLoadingUrl: boolean;
};

const KlydoFileInput = ({
  klydoSecureUrl,
  onFileChange,
  onRemoveFile,
  fileName,
  isLoadingUrl,
}: KlydoFileInputProps) => {
  const { spacing, handleFileChange, palette, handleRemoveFile } =
    useKlydoFileInput({
      onFileChange,
      onRemoveFile,
    });

  return (
    <DSBox sx={{ display: 'flex', flexDirection: 'column', gap: spacing(1) }}>
      <DSBox
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <DSLabelTypography>*Choose File</DSLabelTypography>
        <DSTooltip title={<UploadLoopSpeck />}>
          <DSIconButton
            sx={{
              height: spacing(1),
              '&:hover': {
                backgroundColor: 'transparent',
                color: palette.text.primary,
              },
              color: palette.text.secondary,
              transition: 'color 0.3s ease',
            }}
          >
            <HelpOutline />
          </DSIconButton>
        </DSTooltip>
      </DSBox>
      <DSPaper
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          alignSelf: 'stretch',
          width: '100%',
          height: 'auto',
          padding: spacing(2),
          gap: spacing(1),
        }}
      >
        {!klydoSecureUrl ? (
          isLoadingUrl ? (
            <DSCircularProgress />
          ) : (
            <DSBox
              sx={{
                justifyContent: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: spacing(1),
              }}
            >
              <DSIconButton
                component="label"
                sx={{
                  backgroundColor: palette.action.disabled,
                  fontSize: '24px',
                  padding: '12px',
                  width: spacing(6),
                  boxShadow:
                    '0px 1px 18px 0px rgba(0, 0, 0, 0.12), 0px 6px 10px 0px rgba(0, 0, 0, 0.14), 0px 3px 5px -1px rgba(0, 0, 0, 0.20)',
                }}
              >
                <AddIcon />
                <Input
                  inputProps={{ accept: '.mp4, .gif' }}
                  type="file"
                  onChange={handleFileChange}
                  style={{
                    display: 'none',
                  }}
                />
              </DSIconButton>
              <DSValueTypography color={'rgba(119, 119, 119, 1)'}>
                MP4, Gif
              </DSValueTypography>
            </DSBox>
          )
        ) : (
          <DSBox sx={{ width: '100%' }}>
            <DSBox
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <DSBox
                sx={{
                  flexDirection: 'row',
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing(1),
                  height: spacing(10),
                  width: '90%',
                }}
              >
                <DSBox sx={{ width: spacing(10), height: spacing(10) }}>
                  <KlydoLoopUrl loopUrl={klydoSecureUrl} />
                </DSBox>
                <DSTypography
                  sx={{
                    pointerEvents: 'auto',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    wordWrap: 'break-word',
                    whiteSpace: 'normal',
                    width: '80%',
                    cursor: 'default',
                  }}
                >
                  {fileName}
                </DSTypography>
              </DSBox>

              <DSIconButton
                onClick={handleRemoveFile}
                sx={{
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: palette.text.primary,
                  },
                  color: palette.text.secondary,
                  transition: 'color 0.3s ease',
                }}
              >
                <CloseIcon />
              </DSIconButton>
            </DSBox>
          </DSBox>
        )}
      </DSPaper>
    </DSBox>
  );
};

export default KlydoFileInput;
